import { useMemo } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Tag, Typography } from "antd";
import CustomModal from "components/CustomModal";
import { PRODUCT_INPUT_TYPE } from "config/constants";
import {
  FilterLevelPrice,
  FilterManufacture,
} from "features/Campaign/components/Detail/ProductPromo/components/FilterProduct";
import { t } from "i18next";
import { Link } from "react-router-dom";
import { formatCurrency } from "utils/helperFuncs";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import { SELLING_STATUS } from "pages/App/Product/constants";
import {
  FilterInstockVat,
  FilterStatus,
  FilterVatPercent,
} from "pages/App/Product/Product/List/components/FilterProduct";
import ModalSelectProductBody from "./ModalSelectProductBody";
import "./index.scss";

export function buildModalSelectProductColumns({
  filterProductExtra,
  filterProductPrices,
  params,
  renderProductAttributes,
  renderChildrenInStock,
  warehouses,
  dataManufactures,
}) {
  return [
    {
      title: "THÔNG TIN SẢN PHẨM",
      dataIndex: "code",
      width: 250,
      fixed: "left",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <Link target="_blank" to={`/product/detail/${record?.id}`} className="linked-text">
            <Typography.Link strong>{record?.code}</Typography.Link>
          </Link>
          <CustomTextHiddenLine text={record?.name} line={2} />
        </div>
      ),
    },
    {
      title: t("product.tableTitle.brand"),
      dataIndex: ["manufacturer", "name"],
      align: "center",
      render: (_, record) => <div>{record?.manufacturer?.name ? record.manufacturer.name : "- -"}</div>,
      filterDropdown: <FilterManufacture dataManufactures={dataManufactures} onFilter={filterProductExtra} />,
    },
    {
      title: t("product.tableTitle.warranty"),
      dataIndex: "warranty",
      align: "center",
      render: (_, record) => <div>{record?.warranty ? `${record.warranty} ${t("common.month")}` : "- -"}</div>,
    },
    ...renderProductAttributes(),
    {
      title: "LOẠI % VAT",
      dataIndex: "vatPercent",
      align: "center",
      render: (_, record) => <div>{record.vatPercent >= 0 ? `${record.vatPercent}%` : "KCT"}</div>,
      width: 150,
      editable: "true",
      inputType: PRODUCT_INPUT_TYPE.VAT_PERCENT,
      filterDropdown: <FilterVatPercent onFilter={filterProductExtra} defaultValue={params.filters.vatPercent} />,
    },
    {
      title: "GIÁ CÓ VAT",
      dataIndex: ["productPrices", "priceVat"],
      align: "right",
      render: (_, record) => (
        <div>
          {record?.productPrices?.priceVat ? formatCurrency(record?.productPrices?.priceVat) : formatCurrency(0)}
        </div>
      ),
      editable: "true",
      inputType: PRODUCT_INPUT_TYPE.CURRENCY,
      filterDropdown: (
        <FilterLevelPrice
          onFilter={(priceLevel) => {
            filterProductPrices(priceLevel, "priceVat");
          }}
          defaultValue={params.filters.priceVat}
        />
      ),
    },
    {
      title: <span>{t("product.tableTitle.inStockVat")}</span>,
      align: "center",
      render: (_, record) => (record?.stocks && record?.stocks[0].quantityRealStockHasVat) ?? 0,
      width: 150,
      filterDropdown: <FilterInstockVat onFilter={filterProductExtra} defaultValue={params.filters.realStockHasVat} />,
    },
    {
      title: t("product.tableTitle.inStock"),
      children: renderChildrenInStock(warehouses),
      align: "center",
    },
    {
      title: t("product.tableTitle.inStock"),
      align: "right",
      render: (_, record) => <div>{record?.stocks?.at(0).quantity || "- -"}</div>,
    },
    {
      title: t("product.tableTitle.status"),
      dataIndex: "isActive",
      align: "center",
      editable: "false",
      fixed: "right",
      filterDropdown: <FilterStatus onFilter={filterProductExtra} defaultValue={params?.filters?.sellingStatuses} />,
      render: (_, record) => (
        <div>
          {record?.sellingStatus === SELLING_STATUS.AVAILABLE && (
            <Tag className="custom-tag-status-product-available">Đang kinh doanh</Tag>
          )}
          {record?.sellingStatus === SELLING_STATUS.UNAVAILABLE && (
            <Tag className="custom-tag-status-product-unavailable">Ngừng kinh doanh</Tag>
          )}
          {record?.sellingStatus === SELLING_STATUS.CONTACT && (
            <Tag className="custom-tag-status-product-contact">Liên hệ</Tag>
          )}
        </div>
      ),
    },
  ];
}

function ModalSelectProductDialog({
  visible,
  onCancel,
  onConfirm,
  onOpen,
  trigger,
  showTrigger = false,
  lockCategoryFilter = false,
  selectionType = "checkbox",
  isUpdate = false,
  closable = false,
  hookState,
}) {
  const {
    loadingCheckDuplicateCodes,
    filterItemsTypeOptions,
    rowSelection,
    loadingProduct,
    isFilterSelected,
    selectedRows,
    productData,
    paginationData,
    dataDuplicateCode,
    prodAtt,
    warehouses,
    filterProductByCategoryId,
    handleSearch,
    handleChange,
    onTableChange,
    renderProductAttributes,
    renderChildrenInStock,
    params,
    dataManufactures,
    loadingManufactures,
    listWarehouse,
    loadingWarehouses,
    setParams,
    filterProductExtra,
    filterProductPrices,
    searchType,
    itemsSearchBulk,
    handleChangeSelectSearch,
    handleSelectSearchType,
  } = hookState;

  const columns = useMemo(
    () =>
      buildModalSelectProductColumns({
        filterProductExtra,
        filterProductPrices,
        params,
        renderProductAttributes: () => renderProductAttributes(prodAtt),
        renderChildrenInStock,
        warehouses,
        dataManufactures,
      }),
    [
      filterProductExtra,
      filterProductPrices,
      params,
      prodAtt,
      renderProductAttributes,
      renderChildrenInStock,
      warehouses,
      dataManufactures,
    ]
  );

  const dataSource = isFilterSelected ? selectedRows : productData;

  return (
    <CustomModal
      customComponent={showTrigger ? trigger : undefined}
      selfVisibleControlled={showTrigger}
      message={<Typography.Title level={3}>{t("campaign.combo.addProductToPromotion")}</Typography.Title>}
      footer={false}
      width="90%"
      centered
      closable={closable}
      className="custom-modal-select-product-dialog"
      visible={visible}
      onCancel={onCancel}
      onOpen={onOpen}
      onOke={onConfirm}
      buttonLoading={loadingCheckDuplicateCodes}
      okeButtonLabel={t("campaign.combo.addProduct")}
      children={
        <ModalSelectProductBody
          columns={columns}
          lockCategoryFilter={lockCategoryFilter}
          isUpdate={isUpdate}
          filterItemsTypeOptions={filterItemsTypeOptions}
          rowSelection={rowSelection}
          selectionType={selectionType}
          loadingCheckDuplicateCodes={loadingCheckDuplicateCodes}
          loadingProduct={loadingProduct}
          dataSource={dataSource}
          onTableChange={onTableChange}
          paginationData={paginationData}
          isFilterSelected={isFilterSelected}
          selectedRows={selectedRows}
          dataDuplicateCode={dataDuplicateCode}
          filterProductByCategoryId={filterProductByCategoryId}
          params={params}
          handleSearch={handleSearch}
          searchType={searchType}
          handleSelectSearchType={handleSelectSearchType}
          itemsSearchBulk={itemsSearchBulk}
          handleChangeSelectSearch={handleChangeSelectSearch}
          handleChange={handleChange}
          dataManufactures={dataManufactures}
          loadingManufactures={loadingManufactures}
          listWarehouse={listWarehouse}
          loadingWarehouses={loadingWarehouses}
          prodAtt={prodAtt}
          setParams={setParams}
        />
      }
    />
  );
}

export default ModalSelectProductDialog;

export const defaultModalSelectProductTrigger = (
  <Typography.Link underline>
    <PlusOutlined /> {t("campaign.combo.addProduct")}
  </Typography.Link>
);
